import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/reset-password.scss';
import { useUI } from '../../shared/UI/Uicontext';
import { alertService } from '../../shared/services/alertService';
import { decryptData } from '../../shared/services/cryptoService';
import { getSessionData } from 'utils.session';
import {
  useLazyGetOldPasswordQuery,
  useRequestOtpMutation,
  useOtpUpdateNewPassMutation,
} from './ResetPassword.Service';

const PASSWORD_PATTERN = /^(?=.*[A-Z])(?=.*[0-9])(?=.*[a-z]).{8,15}$/;

const EyeIcon = ({
  show,
  onToggle,
}: {
  show: boolean;
  onToggle: () => void;
}) => (
  <i
    className={show ? 'pi pi-eye-slash' : 'pi pi-eye'}
    onClick={onToggle}
    style={{
      position: 'absolute',
      right: 12,
      cursor: 'pointer',
      color: '#6b7280',
    }}
  />
);

const ErrorBadge = ({ msg }: { msg: string }) => (
  <div className="rp-error-badge">
    <i className="pi pi-times-circle" />
    {msg}
  </div>
);

// ── Password strength scoring, mirrors PrimeNG's built-in meter ──
type Strength = { label: string; percent: number; color: string };

const getPasswordStrength = (pw: string): Strength => {
  if (!pw) return { label: '', percent: 0, color: '#e5e7eb' };

  let score = 0;
  if (pw.length >= 8) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;

  if (score <= 2) return { label: 'Weak', percent: 33, color: '#ef4444' };
  if (score <= 4) return { label: 'Medium', percent: 66, color: '#f59e0b' };
  return { label: 'Strong', percent: 100, color: '#10b981' };
};

// Shared popover shown while a password field has focus
const PasswordStrengthPanel = ({ password }: { password: string }) => {
  const { label, percent, color } = getPasswordStrength(password);
  return (
    <div className="rp-password-note">
      <h6>Password Strength</h6>
      <div className="rp-strength-bar-track">
        <div
          className="rp-strength-bar-fill"
          style={{ width: `${percent}%`, background: color }}
        />
      </div>
      {label && (
        <p className="rp-strength-label" style={{ color }}>
          {label}
        </p>
      )}
      <p className="rp-suggestions-title">Suggestions</p>
      <ul>
        <li>At least one lowercase</li>
        <li>At least one uppercase</li>
        <li>At least one numeric</li>
        <li>Minimum 8 characters</li>
      </ul>
    </div>
  );
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useUI();
  const userName = (getSessionData() as any)?.username ?? '';

  const [getOldPassword] = useLazyGetOldPasswordQuery();
  const [otpPasswordReset] = useRequestOtpMutation();
  const [otpUpdateNewPass] = useOtpUpdateNewPassMutation();

  const [index, setIndex] = useState(0);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [oldTouched, setOldTouched] = useState(false);
  const [newTouched, setNewTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showSuccessOtp, setShowSuccessOtp] = useState(false);
  const [otpMsg, setOtpMsg] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpValue, setOtpValue] = useState('');

  const oldRequiredError = !oldPassword && (submitted || oldTouched);
  const newRequiredError = !newPassword && (submitted || newTouched);
  const newPatternError = !!newPassword && !PASSWORD_PATTERN.test(newPassword);
  const confRequiredError = !confirmPassword && (submitted || confirmTouched);
  const confPatternError =
    !!confirmPassword && !PASSWORD_PATTERN.test(confirmPassword);

  const isFormValid =
    !!oldPassword &&
    !!newPassword &&
    PASSWORD_PATTERN.test(newPassword) &&
    !!confirmPassword &&
    PASSWORD_PATTERN.test(confirmPassword);

  const handleTabChange = (i: number) => {
    setIndex(i);
    if (i === 1) setShowOtpModal(true);
  };

  const handleGetOtp = async () => {
    setSubmitted(true);
    if (!isFormValid) return;

    if (newPassword !== confirmPassword) {
      alertService.error('New Password and Confirm password mismatched');
      return;
    }
    if (confirmPassword === oldPassword) {
      alertService.warning('New Password should not be same as Old Password');
      return;
    }

    try {
      showLoader();
      const oldPassRes: any = await getOldPassword(oldPassword).unwrap();
      hideLoader();

      if (!oldPassRes?.dataStatus) {
        alertService.error(oldPassRes?.message || 'Old password is incorrect');
        return;
      }

      showLoader();
      const result: any = await otpPasswordReset({
        UserId: userName,
        newPassword: confirmPassword,
      }).unwrap();
      hideLoader();

      const res: any = decryptData(result);

      if (res?.dataStatus) {
        const mbl = String(res.result[0]['target_mobile_number']);
        const masked = '*'.repeat(Math.min(6, mbl.length)) + mbl.slice(6);
        setOtpMsg(
          `OTP has been successfully sent to your mobile number - ${masked}. ` +
            'Please enter the OTP within 3 days to reset the password.'
        );
        setShowSuccessOtp(true);

        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setSubmitted(false);
        setOldTouched(false);
        setNewTouched(false);
        setConfirmTouched(false);
      } else {
        alertService.warning(res?.message || 'Failed to send OTP');
      }
    } catch (err: any) {
      hideLoader();
      alertService.error(err?.data?.message || 'Something went wrong');
    }
  };

  const submitOtp = async () => {
    if (!otpValue) {
      alertService.warning('Please Enter the OTP');
      return;
    }

    try {
      showLoader();
      const res: any = await otpUpdateNewPass({
        username: userName,
        otp: otpValue.trim(),
      }).unwrap();
      hideLoader();

      if (res?.dataStatus) {
        alertService.success(res.message || 'Password updated successfully!');
        closeOtpModal();
        localStorage.clear();
        sessionStorage.clear();
        navigate('/auth/login');
      } else {
        alertService.error(res?.message || 'Failed to verify OTP');
      }
    } catch (err: any) {
      hideLoader();
      alertService.error(err?.data?.message || 'Something went wrong');
    }
  };

  const closeOtpModal = () => {
    setIndex(0);
    setShowOtpModal(false);
    setOtpValue('');
  };

  return (
    <div className="reset-password-page">
      <div className="rp-tabs-wrap">
        <div className="rp-tabs">
          <button
            className={`rp-tab ${index === 0 ? 'active' : ''}`}
            onClick={() => handleTabChange(0)}
          >
            Reset Password
          </button>
          <button
            className={`rp-tab ${index === 1 ? 'active' : ''}`}
            onClick={() => handleTabChange(1)}
          >
            OTP Submission
          </button>
        </div>
        <i className="pi pi-angle-right rp-tabs-arrow" />
      </div>

      {index === 0 && (
        <div className="rp-content">
          <div className="rp-title-btn-wrap">
            <button className="rp-title-btn">Reset Password</button>
          </div>
          <div className="rp-form">
            {/* Old password */}
            <div className="rp-row">
              <label className="rp-label">
                Old password <span className="text-danger">*</span>
              </label>
              <div className="rp-field">
                <div className="rp-input-wrap">
                  <input
                    type={showOld ? 'text' : 'password'}
                    className={`form-control rp-input`}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    onBlur={() => setOldTouched(true)}
                  />
                  <EyeIcon
                    show={showOld}
                    onToggle={() => setShowOld((v) => !v)}
                  />
                </div>
                {oldRequiredError && <ErrorBadge msg="Enter Old Password" />}
              </div>
            </div>

            {/* New password */}
            <div className="rp-row">
              <label className="rp-label">
                New password <span className="text-danger">*</span>
              </label>
              <div className="rp-field">
                <div className="rp-input-wrap">
                  <input
                    type={showNew ? 'text' : 'password'}
                    className={`form-control rp-input`}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onBlur={() => setNewTouched(true)}
                  />
                  <EyeIcon
                    show={showNew}
                    onToggle={() => setShowNew((v) => !v)}
                  />
                </div>
                {newRequiredError && <ErrorBadge msg="Enter New Password" />}
                {!newRequiredError && newPatternError && (
                  <ErrorBadge msg="Enter Valid New Password" />
                )}
                <PasswordStrengthPanel password={newPassword} />
              </div>
            </div>

            {/* Confirm password */}
            <div className="rp-row">
              <label className="rp-label">
                Confirm New password <span className="text-danger">*</span>
              </label>
              <div className="rp-field">
                <div className="rp-input-wrap">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    className={`form-control rp-input`}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onBlur={() => setConfirmTouched(true)}
                  />
                  <EyeIcon
                    show={showConfirm}
                    onToggle={() => setShowConfirm((v) => !v)}
                  />
                </div>
                {confRequiredError && (
                  <ErrorBadge msg="Enter Confirm New Password" />
                )}
                {!confRequiredError && confPatternError && (
                  <ErrorBadge msg="Enter Valid Confirm Password" />
                )}
                <PasswordStrengthPanel password={confirmPassword} />
              </div>
            </div>

            {/* GET OTP button */}
            <div className="rp-row rp-row-btn">
              <button className="rp-get-otp-btn" onClick={handleGetOtp}>
                GET OTP
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Sent Successfully dialog */}
      {showSuccessOtp && (
        <div className="otp-modal">
          <div className="otp-success-modal-content">
            <div className="otp-success-header">OTP Sent Successfully</div>
            <div className="otp-success-body">
              <p>{otpMsg}</p>
              <button
                className="btn-ok-otp"
                onClick={() => {
                  setShowSuccessOtp(false);
                  setShowOtpModal(true);
                }}
              >
                Ok
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Submission dialog */}
      {showOtpModal && (
        <div className="otp-modal">
          <div className="otp-modal-content">
            <div className="otp-modal-header">OTP Submission</div>
            <div className="otp-modal-body">
              <div className="otp-row">
                <div className="otp-input-section">
                  <label className="form-label">
                    OTP <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    className="form-control otp-input"
                    placeholder="Enter the OTP"
                    value={otpValue}
                    onChange={(e) =>
                      setOtpValue(e.target.value.replace(/\D/g, ''))
                    }
                  />
                </div>
              </div>
              <div className="otp-footer">
                <button className="btn-submit-otp" onClick={submitOtp}>
                  Submit
                </button>
                <button className="btn-close-otp" onClick={closeOtpModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
